import { toLower, upperFirst } from "lodash";
import { Badge } from "@shared/ui";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";

import { AdminContext, ClientContext } from "../App";
import { getTeam } from "../lib/automatic";
import {
  disableUser,
  enableUser,
  resendInvite,
  updatePermissions,
} from "../lib/teamMemberHandler";
import seedData from "../seed/team.json";
import DeleteUserModal from "./DeleteUserModal";
import DropdownMenu from "./DropdownMenu";
import LoadingSpinner from "./LoadingSpinner";
import UpdatePermissionsModal from "./UpdatePermissionsModal";
import { SelectColumnFilter } from "./table/Filter";
import Table from "./table/Table";

const TeamTable = () => {
  const { promiseInProgress } = usePromiseTracker();
  const [team, setTeam] = useState([]);
  const { selectedClient: clientId, platformClientId } =
    useContext(ClientContext);
  const userRole = useContext(AdminContext);
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);

  // Check if current user is admin or platform-admin
  const isAdmin = userRole === "admin" || userRole === "platform-admin";

  const handleOpenDeleteModal = useCallback((member) => {
    setMemberToDelete(member);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setMemberToDelete(null);
  }, []);

  const handleOpenPermissionsModal = useCallback((member) => {
    setSelectedMember(member);
    setIsPermissionsModalOpen(true);
  }, []);

  const handleClosePermissionsModal = useCallback(() => {
    setIsPermissionsModalOpen(false);
    setSelectedMember(null);
  }, []);

  // Team member action handlers - just regular functions!
  const handleDisableUser = async () => {
    await disableUser(
      memberToDelete,
      clientId,
      handleCloseDeleteModal,
      setTeam
    );
  };

  const handleEnableUser = async () => {
    await enableUser(memberToDelete, clientId, handleCloseDeleteModal, setTeam);
  };

  const handleUpdatePermissions = async (oldRole, newRole) => {
    await updatePermissions(
      selectedMember,
      oldRole,
      newRole,
      clientId,
      handleClosePermissionsModal,
      setTeam
    );
  };

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        Cell: ({ value }) => {
          return value ? upperFirst(value) : "—";
        },
        width: 100,
      },
      {
        Header: "Email",
        accessor: "email",
        Cell: ({ value }) => {
          return value ? upperFirst(value) : "—";
        },
        width: 100,
        // className: "flex-none"
      },
      {
        Header: "Role",
        Filter: SelectColumnFilter,
        accessor: "role",
        Cell: ({ value }) => {
          return (
            <span>
              <Badge status={value} withIcon={false} />
            </span>
          );
        },
        width: 100,
      },
      {
        Header: "Status",
        Filter: SelectColumnFilter,
        accessor: "userStatus",
        Cell: ({ value }) => {
          return <Badge status={toLower(value)} />;
        },
        width: 100,
      },
      ...(isAdmin
        ? [
            {
              Header: "",
              accessor: "actions",
              disableFilters: true,
              canFilter: false,
              Cell: ({ row }) => {
                const member = row.original;
                const isDeactivated =
                  member.userStatus?.toLowerCase() === "deactivated";
                const menuItems = [
                  {
                    label: "Update user permissions",
                    onClick: () => handleOpenPermissionsModal(member),
                  },
                  ...(member.userStatus?.toLowerCase() === "pending"
                    ? [
                        {
                          label: "Resend dashboard invite",
                          onClick: () => resendInvite(member, clientId),
                        },
                      ]
                    : []),
                  {
                    label: isDeactivated ? "Enable user" : "Disable user",
                    onClick: () => handleOpenDeleteModal(member),
                    danger: !isDeactivated,
                  },
                ];

                return (
                  <div className="flex justify-end relative overflow-visible">
                    <DropdownMenu items={menuItems} />
                  </div>
                );
              },
              width: 50,
              className: "flex-none relative overflow-visible",
            },
          ]
        : []),
    ],
    [clientId, handleOpenDeleteModal, handleOpenPermissionsModal, isAdmin]
  );

  useEffect(() => {
    // Use seed data if set to true
    if (process.env.REACT_APP_MOCK === "true") {
      setTeam(seedData);
      return;
    }

    // Retrieve team members and set to state
    if (clientId) {
      trackPromise(
        getTeam(clientId).then((data) => {
          setTeam(data);
        })
      );
    }
  }, [clientId]); // Add clientId as a dependency to re-fetch when client changes

  return (
    <>
      {promiseInProgress === true ? (
        <LoadingSpinner />
      ) : (
        <>
          <Table
            entity="team"
            data={team}
            columns={columns}
            rowBehavior={{ type: "default" }}
            globalFilterPlaceholder={"Enter any team member name or email..."}
            csvFields={["name", "email", "role", "userStatus"]}
            dateFilter={null}
            onRowClick={null}
          />
          <UpdatePermissionsModal
            isOpen={isPermissionsModalOpen}
            onClose={handleClosePermissionsModal}
            member={selectedMember}
            onUpdate={handleUpdatePermissions}
            platformClientId={platformClientId}
            selectedClientId={clientId}
          />
          <DeleteUserModal
            isOpen={isDeleteModalOpen}
            onClose={handleCloseDeleteModal}
            member={memberToDelete}
            onConfirm={
              memberToDelete?.userStatus?.toLowerCase() === "deactivated"
                ? handleEnableUser
                : handleDisableUser
            }
          />
        </>
      )}
    </>
  );
};

export default TeamTable;
